import { useQuery } from '@tanstack/react-query'
import { TrendingDown, TrendingUp, Minus } from 'lucide-react'
import PropTypes from 'prop-types'
import { useMemo } from 'react'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/elements/accordion'
import { Badge } from '@/components/elements/badge'
import { Skeleton } from '@/components/elements/skeleton'
import { QuizResult } from '@/components/QuizResult'
import { getQuizAttempts } from '@/services/student_courses.service'
import { formatTimeAndDate, scoreFormatter } from '@/utils/formatters'

// Compares the most recent attempt's score against the average of prior attempts
// rather than just the immediately previous one. Prior-attempt average smooths
// over one-off bad attempts and reads more honestly as a trajectory.
function attemptTrend(attempts) {
  if (!attempts || attempts.length < 2) return null
  const sorted = [...attempts].sort((a, b) => a.attemptNumber - b.attemptNumber)
  const latest = sorted[sorted.length - 1]
  const prior = sorted.slice(0, -1)
  const priorAvg = prior.reduce((sum, a) => sum + (a.score ?? 0), 0) / prior.length
  const delta = (latest.score ?? 0) - priorAvg
  if (delta > 0.5) return 'improving'
  if (delta < -0.5) return 'regressing'
  return 'plateaued'
}

const TREND_META = {
  improving: { label: 'Improving', variant: 'success', Icon: TrendingUp },
  plateaued: { label: 'Plateaued', variant: 'outline', Icon: Minus },
  regressing: { label: 'Regressing', variant: 'destructive', Icon: TrendingDown },
}

function QuizAttemptsPanel({ contentRecord, entityId, attempts: providedAttempts = null }) {
  const quiz = contentRecord.content?.quiz
  // Skip the per-record fetch when the parent already batch-loaded attempts via
  // ?expand=quiz_attempts. Array form means "data is here, even if empty"; only
  // undefined falls through to the per-record fetch path.
  const { data: fetchedAttempts, isPending } = useQuery({
    queryKey: ['accounts', 'students', 'quiz-attempts', entityId, contentRecord.id],
    queryFn: () => getQuizAttempts(entityId, contentRecord.id),
    enabled: !Array.isArray(providedAttempts),
  })
  const attempts = Array.isArray(providedAttempts) ? providedAttempts : fetchedAttempts

  const summary = useMemo(() => {
    if (!attempts || attempts.length === 0) return null
    const bestAttempt = attempts.reduce(
      (best, a) => ((a.score ?? -1) > (best?.score ?? -1) ? a : best),
      null,
    )
    return {
      best: bestAttempt,
      passedOnFirst: attempts.find((a) => a.attemptNumber === 1)?.result === 'Passed',
      trend: attemptTrend(attempts),
    }
  }, [attempts])

  // Only show the loading skeleton when we're actually waiting on the per-record
  // fetch; the batched-attempts path has data synchronously available.
  if (!Array.isArray(providedAttempts) && isPending) {
    return (
      <div className="flex flex-col gap-2 py-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!attempts || attempts.length === 0) {
    return <p className="py-2 text-sm text-gray-500 italic">No quiz attempts yet.</p>
  }

  const trendMeta = summary?.trend ? TREND_META[summary.trend] : null

  return (
    <div className="flex flex-col gap-2 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600">
          {attempts.length} / {quiz?.numOfRetriesAllowed ?? '∞'} attempts used
        </span>
        {summary?.best && quiz && (
          <Badge variant="secondary">
            Best: {scoreFormatter(summary.best.score, quiz.numQuestionsToDisplay)}
          </Badge>
        )}
        {summary?.passedOnFirst && <Badge variant="success">First-try pass</Badge>}
        {trendMeta && (
          <Badge variant={trendMeta.variant} className="gap-1">
            <trendMeta.Icon className="h-3 w-3" />
            {trendMeta.label}
          </Badge>
        )}
      </div>
      <Accordion type="single" collapsible className="w-full">
        {attempts.map((attempt) => (
          <AccordionItem key={attempt.id} value={attempt.id}>
            <AccordionTrigger>
              <div className="flex items-center gap-3">
                <span className="font-medium">Attempt {attempt.attemptNumber}</span>
                <Badge variant={attempt.result === 'Passed' ? 'success' : 'destructive'}>
                  {attempt.result}
                </Badge>
                {quiz && (
                  <span className="text-sm text-gray-600">
                    {scoreFormatter(attempt.score, quiz.numQuestionsToDisplay)}
                  </span>
                )}
                <span className="text-sm text-gray-400">
                  {formatTimeAndDate(attempt.createdAt)}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {attempt.answers && quiz ? (
                <QuizResult quiz={quiz} gradedQuiz={attempt} />
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Answer details not available for this attempt.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

export default QuizAttemptsPanel

QuizAttemptsPanel.propTypes = {
  contentRecord: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  // When provided, the panel uses these directly and skips the per-record
  // getQuizAttempts fetch. Parents that already batch-loaded via the
  // ?expand=quiz_attempts content-records endpoint pass this in.
  attempts: PropTypes.array,
}
